import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { useYearRange } from '../constants/yearRange';
import { loadDocVocab } from '../utils/docVocab';

// ⛔ THE FOLDERS COME FROM THE TABLE (doc_folders, utils/docVocab), in its order: property-level folders, then
// client-level ones, each with year rows when has_years. Rows are bucketed by folder_key. This replaced three
// hardcoded lists (YEAR_FOLDERS, FLAT_FOLDERS, a separate Annual Reviews row with its own 2026–2028 years) that
// had already drifted — Exemptions & Incentives was drawn twice — and under which a folder missing from the list
// was invisible here whatever was filed into it. A retired folder still shows while it holds documents.
// Year rows come from folder_year_config via useYearRange — see yearRange.js.
const NO_YEAR = '__no_year__';   // pseudo-year row: docs filed with no year_subfolder (contracts, pre-cutoff docs)

export default function DocumentsBubble({ parcel, property, isDark, onClose }) {
  const { years: ALL_YEARS } = useYearRange();
  const [files, setFiles]               = useState([]);
  const [vocab, setVocab]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [openFolder, setOpenFolder]     = useState(null);  // folder name
  const [openYear, setOpenYear]         = useState(null);  // year string
  const bubbleRef                       = useRef(null);

  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(32,41,69,0.98)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', hover: 'rgba(255,255,255,0.04)',
  } : {
    bg: '#ffffff', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', hover: 'rgba(0,0,0,0.03)',
  };

  // Load files from both property and parcel level
  useEffect(() => {
    async function load() {
      setLoading(true);
      const results = [];
      // The pile states (Client Uploads, Need to Review, Other, Task Attachments) are not folders — never listed.
      const v = await loadDocVocab().catch(() => null);
      if (!v) { setVocab(null); setFiles([]); setLoading(false); return; }
      setVocab(v);
      const systemKeys = v.folders.filter(f => f.system).map(f => f.key);

      // Property-level files
      if (property?.id) {
        const { data: propFiles } = await supabase
          .from('file_exchange')
          .select('id, file_name, file_type, type_key, folder_key, year_subfolder, filed_at, storage_path')
          .eq('property_id', property.id)
          // ⛔ NO DIRECTION FILTER — do not add one back. This used to carry
          // .eq('direction','client_to_ntrg'), which showed the LEAST-written direction (3 write sites) and
          // hid the rest: ntrg_to_client (5 — generated TBTs/transmittals) and ntrg_internal (4 — every
          // document filed off a task). It was never the junk filter; the folder exclusion below is, and it
          // already removes the untriaged and miscellaneous rows. Worse, the PARCEL arm of this same
          // component (parcel_documents, below) never filtered direction, so one document was visible at
          // parcel level and invisible at property level in the same bubble.
          // THIRD INSTANCE of this exact bug — see ClientDocuments.js:6, where it was found and deliberately
          // avoided ("filter by direction ... the write path worked and nothing could read it back").
          // DocumentsBrowser doesn't filter direction either. Direction describes who SENT a document, never
          // whether it belongs to the property.
          .not('folder_key', 'in', `(${systemKeys.join(',')})`)
          .order('filed_at', { ascending: false });
        if (propFiles) results.push(...propFiles.map(f => ({ ...f, _level: 'property' })));
      }

      // Parcel-level files via parcel_documents
      if (parcel?.id) {
        const { data: parcelDocs } = await supabase
          .from('parcel_documents')
          .select('*, file_exchange(id, file_name, file_type, type_key, folder_key, year_subfolder, filed_at, storage_path)')
          .eq('parcel_id', parcel.id)
          .order('created_at', { ascending: false });
        if (parcelDocs) {
          parcelDocs.forEach(pd => {
            const fx = pd.file_exchange;
            if (fx && !results.find(r => r.id === fx.id)) {
              results.push({ ...fx, folder_key: pd.folder_key || fx.folder_key, year_subfolder: pd.year_subfolder || fx.year_subfolder, _level: 'parcel' });
            }
          });
        }
      }

      // Display the type's CURRENT name from the table (a rename shows everywhere at once).
      setFiles(results.map(f => ({ ...f, _typeName: v.type(f.type_key)?.name || f.file_type || null })));
      setLoading(false);
    }
    load();
  }, [parcel?.id, property?.id]);

  // Click outside to close
  useEffect(() => {
    function handleClick(e) {
      if (bubbleRef.current && !bubbleRef.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  function getFilesForFolder(folderKey, year) {
    return files.filter(f => {
      const folderMatch = f.folder_key === folderKey;
      if (!year) return folderMatch;
      if (year === NO_YEAR) return folderMatch && !f.year_subfolder;
      return folderMatch && (f.year_subfolder === year || String(f.year_subfolder) === String(year));
    });
  }

  function getFolderCount(folderKey) {
    return files.filter(f => f.folder_key === folderKey).length;
  }

  async function openFile(f) {
    if (!f.storage_path) return;
    const url = await fileUrl(f.storage_path, 'open').catch(() => null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  const folderColor = (count) => count > 0 ? 'var(--red)' : (isDark ? '#374060' : 'var(--line)');
  const folderTextColor = (count) => count > 0 ? 'var(--red)' : (isDark ? '#4a5068' : '#9ca3af');

  function FolderRow({ folder, years }) {
    const name = folder.key;                  // the key drives matching and open-state; folder.name is displayed
    const hasYears = folder.has_years;
    const count = getFolderCount(name);
    const isOpen = openFolder === name;
    // "No year" leads the list — a doc that isn't year-specific (a contract, anything pre-cutoff) reads at the
    // top, not buried after 2028. Rendered ONLY when such files exist: unlike the fixed year rows, an always-on
    // empty "No year" row is noise, not a slot anyone files into.
    const yearRows = hasYears
      ? [...(getFilesForFolder(name, NO_YEAR).length ? [NO_YEAR] : []), ...years]
      : [];
    return (
      <div>
        <div
          onClick={() => { setOpenFolder(isOpen ? null : name); setOpenYear(null); }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 14px', cursor: 'pointer', borderRadius: '7px', marginBottom: '2px',
            background: isOpen ? (isDark ? 'rgba(180,0,0,0.1)' : 'rgba(180,0,0,0.07)') : 'transparent',
            transition: 'background 0.12s',
          }}
          onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = c.hover; }}
          onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>📁</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: count > 0 ? c.text : (isDark ? '#4a5068' : '#9ca3af') }}>{folder.name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {count > 0 && (
              <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px', background: 'rgba(180,0,0,0.15)', color: 'var(--red)', border: '1px solid rgba(180,0,0,0.3)' }}>
                {count}
              </span>
            )}
            <span style={{ fontSize: '10px', color: isDark ? '#4a5068' : '#9ca3af' }}>{isOpen ? '▲' : '▶'}</span>
          </div>
        </div>

        {isOpen && (
          <div style={{ marginLeft: '12px', marginBottom: '6px' }}>
            {hasYears ? (
              // Year subfolders
              yearRows.map(year => {
                const yearFiles = getFilesForFolder(name, year);
                const isYearOpen = openYear === `${name}-${year}`;
                return (
                  <div key={year}>
                    <div
                      onClick={() => setOpenYear(isYearOpen ? null : `${name}-${year}`)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '7px 12px', cursor: 'pointer', borderRadius: '6px', marginBottom: '2px',
                        background: isYearOpen ? (isDark ? 'rgba(96,165,250,0.08)' : 'rgba(96,165,250,0.06)') : 'transparent',
                      }}
                      onMouseEnter={e => { if (!isYearOpen) e.currentTarget.style.background = c.hover; }}
                      onMouseLeave={e => { if (!isYearOpen) e.currentTarget.style.background = 'transparent'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '11px' }}>{year === NO_YEAR ? '📄' : '📅'}</span>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: yearFiles.length > 0 ? (isDark ? '#a8b0cc' : '#4a5068') : (isDark ? '#374060' : '#d1d5db') }}>{year === NO_YEAR ? 'No year' : year}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {yearFiles.length > 0 && (
                          <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '8px', background: 'rgba(96,165,250,0.12)', color: '#60a5fa' }}>{yearFiles.length}</span>
                        )}
                        {yearFiles.length > 0 && <span style={{ fontSize: '10px', color: isDark ? '#4a5068' : '#9ca3af' }}>{isYearOpen ? '▲' : '▶'}</span>}
                      </div>
                    </div>
                    {isYearOpen && yearFiles.length > 0 && (
                      <div style={{ marginLeft: '10px', marginBottom: '4px' }}>
                        {yearFiles.map(f => <FileRow key={f.id} f={f} onOpen={openFile} isDark={isDark} c={c} />)}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Flat — show files directly
              getFilesForFolder(name).length === 0 ? (
                <div style={{ padding: '8px 12px', fontSize: '11px', color: isDark ? '#4a5068' : '#9ca3af', fontStyle: 'italic' }}>No files filed here yet</div>
              ) : (
                getFilesForFolder(name).map(f => <FileRow key={f.id} f={f} onOpen={openFile} isDark={isDark} c={c} />)
              )
            )}
          </div>
        )}
      </div>
    );
  }

  const totalFiles = files.length;
  // Active folders of a level, plus any retired one that still holds documents here.
  const shownFolders = (level) => (vocab ? vocab.foldersByLevel(level, { includeRetired: true }) : [])
    .filter(f => f.active || getFolderCount(f.key) > 0);

  return (
    <div ref={bubbleRef} style={{
      position: 'fixed', top: '80px', right: '20px', width: '320px',
      maxHeight: 'calc(100vh - 100px)', overflowY: 'auto',
      background: isDark ? 'rgba(26,34,56,0.97)' : '#ffffff',
      border: `1px solid ${c.border}`,
      borderRadius: '14px',
      boxShadow: isDark ? '0 8px 40px rgba(0,0,0,0.5)' : '0 8px 40px rgba(0,0,0,0.15)',
      zIndex: 9999,
      animation: 'bubbleIn 0.2s ease',
      fontFamily: "'Public Sans',system-ui,sans-serif",
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: isDark ? 'rgba(26,34,56,0.97)' : '#ffffff', zIndex: 1, borderRadius: '14px 14px 0 0' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: c.text }}>📂 Documents</div>
          <div style={{ fontSize: '11px', color: c.muted, marginTop: '2px' }}>
            {parcel?.parcel_name || property?.name} · {totalFiles} file{totalFiles !== 1 ? 's' : ''}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '6px', color: '#f87171', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ padding: '10px' }}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: c.muted }}>⏳ Loading files...</div>
        ) : !vocab ? (
          <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: 'var(--red)' }}>Document folders could not be loaded — reload and try again.</div>
        ) : (
          <>
            {/* Property Folders header */}
            <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 14px 4px', marginBottom: '2px' }}>Property Folders</div>

            {shownFolders('property').map(folder => (
              <FolderRow key={folder.key} folder={folder} years={ALL_YEARS} />
            ))}

            {/* Client Folders */}
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '10px 14px 4px', marginBottom: '2px' }}>Client Folders</div>
            {shownFolders('client').map(folder => (
              <FolderRow key={folder.key} folder={folder} years={ALL_YEARS} />
            ))}
          </>
        )}
      </div>

      <style>{`
        @keyframes bubbleIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function FileRow({ f, onOpen, isDark, c }) {
  const levelColor = f._level === 'parcel' ? '#34d399' : '#60a5fa';
  const levelLabel = f._level === 'parcel' ? 'Parcel' : 'Property';
  return (
    <div
      onClick={() => onOpen(f)}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '7px 10px', borderRadius: '6px', cursor: 'pointer',
        marginBottom: '2px', transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span style={{ fontSize: '13px', flexShrink: 0 }}>📄</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '11px', fontWeight: '600', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</div>
        <div style={{ fontSize: '10px', color: c.muted, marginTop: '1px' }}>
          {f._typeName || 'Document'}
          {f.filed_at && ` · ${new Date(f.filed_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`}
        </div>
      </div>
      <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '8px', background: `${levelColor}18`, color: levelColor, border: `1px solid ${levelColor}30`, flexShrink: 0 }}>
        {levelLabel}
      </span>
    </div>
  );
}
